import {
  BOND_DOCUMENT_STATUSES,
  BOND_KINDS,
  bondDocumentStatusLabel,
  bondKindLabel,
  documentCategoryLabel,
  documentSourceLabel,
  type BondDocumentStatus,
} from "@/lib/bondStateModel";
import {
  POST_SUBMIT_DOCUMENT_TYPES,
  type BondDocument,
} from "@/lib/brokerBonds";
import {
  uploadableTypes,
  type DocumentTypeInfo,
} from "@/lib/brokerDocumentTypes";
import { formatShortDate } from "@/lib/listFormat";

// BRK-S424 — the submission page's Documents tab, as pure reads of the order's
// documents list. Each row's status, source and related bond are PMS's
// (PMS-S843, typed on `BondDocument`); nothing here decides a
// status — a `required` row is Signal's request, served on the list, and it
// turns `received` only when a re-read says so (NN #7).

/** Rows the Submission Profile shows before "View all N documents in
 *  Submission Documents ›" (the October wireframe, BRK-S502). The full tab
 *  lists every document. */
export const PROFILE_DOCUMENTS_SHOWN = 5;

/** A status chip: the wireframe's word and a key on the shared badge palette
 *  (`statusBadgeClass`). */
export interface DocumentStatusChip {
  label: string;
  tone: string;
}

const STATUS_TONES: Record<BondDocumentStatus, string> = {
  // Red (BRK-S502): the chip renders `danger` in the functional red.
  required: "danger",
  received: "submitted",
  issued: "approved",
  released: "draft",
};

/**
 * The chip for a row's `status`: one of the four words, a neutral "Status
 * unavailable" for a value this build has not adopted, and NO chip when PMS
 * published no status at all (a list from before PMS-S843) — a row with
 * nothing to say is not a row whose status is unknown.
 */
export function documentStatusChip(raw: unknown): DocumentStatusChip | null {
  if (raw === undefined || raw === null) return null;
  const known = BOND_DOCUMENT_STATUSES.find((s) => s === raw);
  return {
    label: bondDocumentStatusLabel(raw),
    tone: known ? STATUS_TONES[known] : "draft",
  };
}

export interface SubmissionDocumentRow {
  id: string;
  /** The filename, or a required row's name for what Signal asked for. */
  name: string;
  type: string;
  /** The served category's label (Financials, Application, Project, Bond,
   *  Correspondence, Rider — PMS-S843, BRK-S502). */
  category: string;
  relatedBond: string;
  source: string;
  /** m/d/yyyy (D-10); a dash when unreadable. */
  date: string;
  uploadedAt: string;
  chip: DocumentStatusChip | null;
  /** Signal is waiting on it: Upload, never Download. */
  isRequired: boolean;
  /** Bytes are on file: Download. */
  hasFile: boolean;
  document: BondDocument;
}

function relatedBondLabel(raw: unknown): string {
  if (raw === undefined || raw === null) return "—";
  const kind = BOND_KINDS.find((k) => k === raw) ?? null;
  // An unknown kind still names a bond rather than reading as "none".
  return bondKindLabel(kind);
}

/**
 * The rows, REQUIRED first (what Signal is waiting on must never fall behind
 * "Show more"), then newest first — the list is served oldest first.
 */
export function submissionDocumentRows(
  documents: readonly BondDocument[],
): SubmissionDocumentRow[] {
  const rows = documents.map((d): SubmissionDocumentRow => {
    const isRequired = d.status === "required";
    return {
      id: d.documentId,
      name: d.filename,
      type: d.type,
      category: documentCategoryLabel(d.category),
      relatedBond: relatedBondLabel(d.relatedBond),
      source:
        d.source === undefined || d.source === null
          ? "—"
          : documentSourceLabel(d.source),
      date: formatShortDate(d.uploadedAt),
      uploadedAt: d.uploadedAt,
      chip: documentStatusChip(d.status),
      isRequired,
      hasFile: !isRequired,
      document: d,
    };
  });
  return rows.sort(
    (a, b) =>
      Number(b.isRequired) - Number(a.isRequired) ||
      b.uploadedAt.localeCompare(a.uploadedAt),
  );
}

/** How many rows Signal is waiting on — the tab strip's badge. */
export function requiredDocumentCount(
  documents: readonly BondDocument[] | null,
): number {
  return (documents ?? []).filter((d) => d.status === "required").length;
}

/**
 * What the upload dialog's type select offers: the types PMS SERVED as
 * uploadable, then the post-submit named types (PMS-S843), then
 * any type a required row asks for — so a row's Upload can always preselect
 * its own type. Each once, labelled from PMS's vocabulary.
 */
export function documentUploadTypes(
  served: readonly DocumentTypeInfo[],
  documents: readonly BondDocument[] | null,
  labelFor: (type: string) => string,
): { value: string; label: string }[] {
  const keys = [
    ...uploadableTypes(served).map((t) => t.key as string),
    ...POST_SUBMIT_DOCUMENT_TYPES,
    ...(documents ?? [])
      .filter((d) => d.status === "required")
      .map((d) => d.type),
  ];
  return [...new Set(keys)].map((value) => ({
    value,
    label: labelFor(value),
  }));
}
